import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../environments/environment.local';
import { DatePipe, isPlatformBrowser } from '@angular/common';
import { AUCTION_STAGE_LABELS, AUCTION_TYPE, QUOTA_LEVEL, USER_ROLE } from './constants';
import { USER_ROLES } from './constants';
import { URLs } from './URL';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private callGetAuctionNumberSubject = new Subject<void>();
  callGetAuctionNumber$ = this.callGetAuctionNumberSubject.asObservable();
  private isBrowser: boolean;

  constructor(private http: HttpClient, private datePipe: DatePipe, @Inject(PLATFORM_ID) private platformId: any, private readonly userService: UserService) {

    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  convertDateToHumanReadable(epoch: Date): string | null {
    return this.datePipe.transform(epoch, 'yyyy-MM-dd hh:mm:ss a');
  }

  getStatusLabel(stage: number) {
    return AUCTION_STAGE_LABELS[stage]
  }

  getLoggedInUserId(): string {
    return this.isBrowser ? localStorage.getItem('userId') ?? '' : '';
  }

  getLoggedInUserEmail() {
    return localStorage.getItem('email');
  }

  getQuotaLevel() {
    return this.isBrowser ? localStorage.getItem('quotaLevel') : '';
  }

  getTenantIdLong() {
    return this.isBrowser ? Number(localStorage.getItem('tenantId')) : 0;
  }

  getRole() {
    return this.isBrowser ? Number(localStorage.getItem('userRole')) : 0;
  }

  newAuctionNumber() {
    this.callGetAuctionNumberSubject.next();
  }

  getAuctionType(): number {
    if (
      this.getRole() == USER_ROLE.SUPPLIER ||
      this.getRole() == USER_ROLE.ADMIN_SUPPLIER
    )
      return AUCTION_TYPE.FORWARD;
    else return AUCTION_TYPE.REVERSE;
  }

  getTimeString(date: Date): string {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? 0 + minutes : minutes;
    const strTime = hours + ':' + minutes + ' ' + ampm;
    return strTime;
  }

  getRoleNameById(roleId: number): string | undefined {
    const role = USER_ROLES.find(userRole => userRole.roleId === roleId);
    return role ? role.role : undefined;
  }

  getRoleIdByName(roleName: string): number | undefined {
    const role = USER_ROLES.find(userRole => userRole.role === roleName);
    return role ? role.roleId : undefined;
  }

  private getDefaultHeaders() {
    return new HttpHeaders().set('tenantId', localStorage.getItem('tenantId')!).append('Authorization', 'Bearer ' + this.userService.getToken());
  }

  login(credentials: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/user/login`, credentials)
  }

  requestLoginOtp(id: string): Observable<any> {
    const headers = new HttpHeaders().set('token', id);
    return this.http.post<any>(`${environment.apiUrl}/user/sendotptoexternaluserviamail`, {}, { headers });
  }

  verifyLoginOtp(id: string, otp: string): Observable<any> {
    const headers = new HttpHeaders().set('token', id);
    return this.http.post<any>(`${environment.apiUrl}/user/loginwithotp`, { otp }, { headers });
  }

  fetchAuctionFormOptions(direction: 'FORWARD' | 'REVERSE'): Observable<any> {
    const headers = this.getDefaultHeaders();
    const url = `${environment.auctionServiceUrl}/auction/fetchAuctionFormOptions?direction=${direction}`;
    return this.http.get<any>(url, { headers })
  }

  fetchAllAuctions(): Observable<any> {
    const headers = this.getDefaultHeaders()
    return this.http.get<any>(`${environment.auctionServiceUrl}/auction/fetchAllAuctions`, { headers })
  }

  saveAuctionBasicDetails(auctionBasicDetails: any): Observable<any> {
    const headers = this.getDefaultHeaders()
    return this.http.post<any>(`${environment.auctionServiceUrl}/auction/save/basicAuctionDetails`, auctionBasicDetails, { headers })
  }

  saveAuctionComprehensiveDetails(auctionComprehensiveDetails: any): Observable<any> {
    const headers = this.getDefaultHeaders()
    return this.http.post<any>(`${environment.auctionServiceUrl}/auction/save/comprehensiveAuctionDetails`, auctionComprehensiveDetails, { headers })
  }

  // private apiUrl = `${environment.auctionServiceUrl}/auction/fetchAllAuctions`;

  // getData(): Observable<any> {
  //   const headers = new HttpHeaders({
  //     'Content-Type': 'application/json',
  //     'tenantId': "2222"
  //   });
  //   return this.http.get<any>(this.apiUrl, { headers: headers });
  // }

  getAuctionNumber(): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'tenantId': this.userService.getTenantId(),
      'Authorization': 'Bearer ' + this.userService.getToken()
    });
    return this.http.get<any>(`${environment.auctionCentralService}/auction/generate/auction_no`, { headers });
  }

  fetchBiddersList(categoryCode: string, businessId: number, searchString?: string): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    // let url = `${environment.auctionServiceUrl}/bidder/list?categoryCode=${categoryCode}&businessId=${businessId}`;
    // if (searchString) {
    //   url += `&searchString=${searchString}`;
    // }
    // return this.http.get<any>(url, { headers });
    let url = `${environment.auctionServiceUrl}/bidder/list`
    const biddersListRequest = {
      "categoryCode": categoryCode,
      "businessId": businessId,
      "userId": this.getLoggedInUserId(),
      "searchString": searchString
    }
    return this.http.post<any>(url, biddersListRequest, { headers })
  }

  mapBidders(mapBiddersRequest: any): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.auctionServiceUrl}/auction/map/bidder`, mapBiddersRequest, { headers })
  }

  addItems(itemsList: any): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.auctionServiceUrl}/auction/addItems`, itemsList, { headers })
  }

  getAuctionDetails(auctionNumber: string): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get<any>(`${environment.auctionServiceUrl}/auction/getAuctionDetails?auctionNumber=${auctionNumber}`, { headers });
  }

  getAuctionItemsList(auctionNumber: string, pageNo: number = -1, pageSize: number = 20, searchText: string = ''): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    let url = `${environment.auctionServiceUrl}/auction/fetchItems?auctionNumber=${auctionNumber}&pageNo=${pageNo}&pageSize=${pageSize}`;
    if (searchText) {
      url += `&searchText=${encodeURIComponent(searchText)}`;
    }
    return this.http.get<any>(url, { headers });
  }

  getAuctionBasicDetails(auctionNumber: string): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get<any>(`${environment.auctionServiceUrl}/auction/getAuctionBasicDetails?auctionNumber=${auctionNumber}`, { headers });
  }

  submitBid(bid: any): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.biddingServiceUrl}/bidder/submit/bid`, bid, { headers })
  }

  uploadDocument(file: File, auctionNumber: string): Observable<any> {
    const headers = new HttpHeaders({
      tenantId: localStorage.getItem('tenantId') || '',
    }).append('Authorization', 'Bearer ' + this.userService.getToken());
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${environment.auctionServiceUrl}/document/upload?auctionNumber=${auctionNumber}&uploadedBy=${localStorage.getItem('email')}`, formData, { headers });
  }

  getUploadedDocs(auctionNumber: string) {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get<any>(`${environment.auctionServiceUrl}/document/fetchAuctionDocuments?auctionNumber=${auctionNumber}`, { headers });
  }

  downloadDoc(filePath: string, documentName: string): Observable<HttpResponse<Blob>> {
    const headers = new HttpHeaders()
      .set('tenantId', localStorage.getItem('tenantId') || '')
      .append('Authorization', 'Bearer ' + this.userService.getToken());

    const encodedFilePath = encodeURIComponent(filePath);
    const encodedDocName = encodeURIComponent(documentName);

    const url = `${environment.auctionServiceUrl}/document/download?filePath=${encodedFilePath}&documentName=${encodedDocName}`;

    return this.http.get(url, {
      headers,
      responseType: 'blob',
      observe: 'response'
    });
  }

  getAllCategories(searchString?: string): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    let url = `${environment.auctionServiceUrl}/category/getAllCategories`;
    if (searchString) {
      url += `?searchString=${searchString}`;
    }
    // return this.http.get<any>(`${environment.auctionServiceUrl}/category/getAllCategories`, { headers });
    return this.http.get<any>(url, { headers });
  }

  getComprehensiveDetails(auctionNumber: string): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get<any>(`${environment.auctionServiceUrl}/auction/getComprehensiveDetails?auctionNumber=${auctionNumber}`, { headers });
  }

  fetchAuctionsByStage(auctionListingRequest: any, pageNo: number, pageSize: number): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.auctionServiceUrl}/auction/fetch/auctionsByStage?pageNo=${pageNo}&pageSize=${pageSize}`, auctionListingRequest, { headers });
  }

  getMappedBiddersList(auctionNumber: string): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get<any>(`${environment.auctionServiceUrl}/auction/get/mapped/biddersList?auctionNumber=${auctionNumber}`, { headers })
  }

  updateAuctionStatus(auctionNumber: string, auctionStage: number): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    const updatedBy = localStorage.getItem('email');
    return this.http.post<any>(`${environment.auctionServiceUrl}/auction/update/status?auctionNo=${auctionNumber}&auctionStage=${auctionStage}&updatedBy=${updatedBy}`, null, { headers })
  }

  acceptTermsAndConditions(auctionNumber: string, userId: string): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.biddingServiceUrl}/bidder/acceptTnc?auctionNumber=${auctionNumber}&userId=${userId}`, null, { headers })
  }

  hasUserAcceptedTerms(auctionNumber: string, userId: string): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get<any>(`${environment.biddingServiceUrl}/bidder/checkAcceptance?auctionNumber=${auctionNumber}&userId=${userId}`, { headers })
  }

  removeMappedBidders(auctionNumber: string, biddersList: any) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.auctionServiceUrl}/auction/remove/mappedBidders?auctionNumber=${auctionNumber}`, biddersList, { headers })
  }

  deleteItem(itemId: number) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.delete<any>(`${environment.auctionServiceUrl}/auction/deleteItem?itemId=${itemId}`, { headers })
  }

  updateLoadingFactor(isLoadingFactor: boolean, auctionNumber: string) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.put<any>(`${environment.auctionServiceUrl}/auction/update/loading-factor?isLoadingFactor=${isLoadingFactor}&auctionNumber=${auctionNumber}`, null, { headers })
  }

  downloadReport(auctionNumber: string, auctionType: number, auctionTitle: string): Observable<HttpResponse<Blob>> {
    const headers = new HttpHeaders()
      .set('tenantId', this.userService.getTenantId())
      .append('Authorization', 'Bearer ' + this.userService.getToken());

    const encodedAuctionTitle = encodeURIComponent(auctionTitle);

    return this.http.get(
      `${environment.reportsUrl}/auction-report/generate/report?auctionTitle=${encodedAuctionTitle}&auctionNumber=${auctionNumber}&auctionType=${auctionType}`,
      {
        headers,
        responseType: 'blob',
        observe: 'response'
      }
    );
  }


  //   getBidderRankAfterRefresh(auctionNumber: string, bidderId: string, auctionType: number) {
  //     const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
  //     return this.http.post<any>(`${environment.biddingServiceUrl}/bidder/get/bidder/rank?auctionNumber=${auctionNumber}&bidderId=${bidderId}&auctionType=${auctionType}`, null, { headers });
  //   }

  //   getAuctioneerBiddingHallData(auctionNumber: string, auctionType: number) {
  //     const headers = new HttpHeaders() .set('tenantId', this.userService.getTenantId()) .append('Authorization', 'Bearer ' + this.userService.getToken());
  //     return this.http.post<any>(`${environment.biddingServiceUrl}/bidder/get/auction/ranks?auctionNumber=${auctionNumber}&auctionType=${auctionType}`, null, { headers });
  // =======
  getBidderRankAfterRefresh(auctionNumber: string, bidderId: string, auctionType: number, itemIds: Set<number>) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    const itemIdsArray = Array.from(itemIds);
    return this.http.post<any>(`${environment.biddingServiceUrl}/bidder/get/bidder/rank?auctionNumber=${auctionNumber}&bidderId=${bidderId}&auctionType=${auctionType}`, itemIdsArray, { headers });
  }

  // getAuctioneerBiddingHallData(auctionNumber: string, auctionType: number, itemIds:Set<number> ) {
  //   const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId());
  //   const itemIdsArray = Array.from(itemIds);
  //   return this.http.post<any>(`${environment.biddingServiceUrl}/bidder/get/auction/ranks?auctionNumber=${auctionNumber}&auctionType=${auctionType}`, itemIdsArray,{ headers });
  // }

  getAuctionEndTime(auctionNumber: string) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.biddingServiceUrl}/bidder/extended/auction/time?auctionNumber=${auctionNumber}`, null, { headers })
  }

  getDeliveryRegions(pincode: string, district: string, pageNo: number = 1) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get(`${environment.auctionServiceUrl}/auction/getDeliveryRegion?pincode=${pincode}&district=${district}&pageNo=${pageNo}`, { headers })
  }

  getCategoryNameFromCategoryCode(categoryCode: string) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get(`${environment.auctionServiceUrl}/category/getCategoryName?categoryCode=${categoryCode}`, { headers });
  }

  uploadImages(files: File[]): Observable<any> {
    const headers = new HttpHeaders({
      tenantId: localStorage.getItem('tenantId') || '',
    }).append('Authorization', 'Bearer ' + this.userService.getToken());
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    return this.http.post<any>(`${environment.auctionServiceUrl}/auction/upload/images?uploadedBy=${this.getLoggedInUserId()}`, formData, { headers });
  }

  getItemImages(itemId: number): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    let url = `${environment.auctionServiceUrl}/item/get/images?itemId=${itemId}`;
    return this.http.get<any>(url, { headers });
  }

  getItemSpecifications(itemId: number): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    let url = `${environment.auctionServiceUrl}/item/get/specifications?itemId=${itemId}`;
    return this.http.get<any>(url, { headers });
  }

  deleteItemSpecifications(specificationId: string) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.delete<any>(`${environment.auctionServiceUrl}/item/delete/specifications?specificationId=${specificationId}`, { headers })
  }

  saveProductLead(productLead: any) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.auctionCentralService}` + URLs.SAVE_PRODUCT_LEAD, productLead, { headers });
  }

  saveAuctionExtensionRequest(auctionExtensionRequest: any) {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.auctionCentralService}` + URLs.SAVE_AUCTION_EXTENSION_REQUEST, auctionExtensionRequest, { headers });
  }

  getQuotaDetails() {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    let url = `${environment.auctionCentralService}` + URLs.GET_QUOTA_DETAILS;
    if (this.getQuotaLevel() == QUOTA_LEVEL.TENANT) url += this.userService.getTenantId();
    else url += this.getLoggedInUserId();
    return this.http.get(url, { headers });
  }

  getLfVariables() {
    const headers = new HttpHeaders().set('tenantId', this.userService.getTenantId()).append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get(`${environment.auctionServiceUrl}` + URLs.GET_LF_VARIABLES, { headers })
  }

  downloadImage(url: string, fileName: string): void {
    this.http.get(url, { responseType: 'blob' }).subscribe(
      (response: Blob) => {
        // Create a new Blob URL
        const downloadLink = document.createElement('a');
        const objectUrl = URL.createObjectURL(response);
        downloadLink.href = objectUrl;
        downloadLink.download = fileName;  // Set the file name for the download
        downloadLink.click();

        // Clean up the object URL
        URL.revokeObjectURL(objectUrl);
      },
      (error) => {
        console.error('Error downloading image:', error);
      }
    );
  }

  public isAuctionOwnedByUser(auctionId: string): Observable<boolean> {
    const headers = new HttpHeaders({
      tenantId: this.userService.getTenantId(),
    }).append('Authorization', 'Bearer ' + this.userService.getToken());
    const email = localStorage.getItem('email');
    let url = `${environment.auctionServiceUrl}/auction/${auctionId}/is-owner?userEmailId=${email}`;
    return this.http.get<boolean>(url, { headers });
  }

  addNewBidder(bidderData: any): Observable<any> {
    console.log('Adding new bidder:', bidderData);
    const headers = this.getDefaultHeaders();
    return this.http.post<any>(`${environment.apiUrl}/user/create`, bidderData, { headers });
  }

  getUserDetailsByEmail(email: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get<any>(`${environment.apiUrl}/user/details?email=${email}`, { headers });
  }

  mapUser(payload: any): Observable<any> {
    const headers = new HttpHeaders()
      .set('tenantId', localStorage.getItem('tenantId') || '')
      .set('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.post<any>(`${environment.apiUrl}/category/map-user`, payload, { headers });
  }
  
  submitContactForm(contactFormData: any): Observable<any> {
    return this.http.post<any>(`${environment.globalInternalUrl}/contact-us`, contactFormData);
  }

  registerUser(payload: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/user`, payload);
  }

  createCategory(categoryData: any): Observable<any> {
    const headers = this.getDefaultHeaders();
    return this.http.post<any>(`${environment.apiUrl}/categories/create`, categoryData, { headers });
  }

  bulkUploadItems(auctionNumber: string, auctionBidSubmissionAtLevel: number, file: File): Observable<any> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    const formData = new FormData();
    formData.append('file', file);
    formData.append('auctionNumber', auctionNumber);
    const levelStr = auctionBidSubmissionAtLevel === 1 ? 'GROSS' : 'ITEM';
    formData.append('auctionBidSubmissionAtLevel', levelStr);
    formData.append('emailId', localStorage.getItem('email') || '');

    return this.http.post<any>(`${environment.auctionServiceUrl}/auction/addItems/bulkUpload`, formData, { headers });
  }

  getBulkUploadProgress(uploadId: string): Observable<BulkUploadProgressResponse> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get<BulkUploadProgressResponse>(`${environment.auctionServiceUrl}/bulkUpload/${uploadId}/progress`, { headers });
  }

  downloadFailedRecords(uploadId: string): Observable<Blob> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get(`${environment.auctionServiceUrl}/bulkUpload/${uploadId}/download-failed`, { headers, responseType: 'blob' });
  }

  downloadItemTemplate(auctionNumber: string): Observable<HttpResponse<Blob>> {
    const headers = new HttpHeaders().set('tenantId', localStorage.getItem('tenantId') || '').append('Authorization', 'Bearer ' + this.userService.getToken());
    return this.http.get(`${environment.auctionServiceUrl}/auction/addItems/template?auctionNumber=${auctionNumber}`, {
      headers,
      responseType: 'blob',
      observe: 'response'
    });
  }
}

export interface BulkUploadProgressResponse {
  percentage: number;
  status: string;
  successCount: number;
  failureCount: number;
  totalRecords: number;
  uploadId?: string;
  errorMessage?: string;
}
